import React, {
  createContext, useContext, useState, useEffect,
  useRef, useCallback, useMemo
} from "react";
import { useAuth } from "./AuthProvider";
import api from './axiosInstance';
import logger from "./utils/logger";

// ─── Split into two contexts to limit re-render blast radius ────────────────
const AppStateContext = createContext();
const AppActionContext = createContext();

export const useAppState = () => useContext(AppStateContext);
export const useAppAction = () => useContext(AppActionContext);
// Keep a convenience combined hook for backwards compat
export const useApp = () => ({ ...useAppState(), ...useAppAction() });

// ─── Stable empty objects so referential equality holds ─────────────────────
const EMPTY_COLUMNS = Object.freeze({
  "UPCOMING": { id: "UPCOMING", title: "UPCOMING", taskIds: [] },
  "IN-PROGRESS": { id: "IN-PROGRESS", title: "IN-PROGRESS", taskIds: [] },
  "FOR-TESTING": { id: "FOR-TESTING", title: "FOR-TESTING", taskIds: [] },
  "COMPLETED": { id: "COMPLETED", title: "COMPLETED", taskIds: [] },
});
const COLUMN_ORDER = Object.freeze(["UPCOMING", "IN-PROGRESS", "FOR-TESTING", "COMPLETED"]);
const EMPTY_BOARD = Object.freeze({ tasks: {}, columns: EMPTY_COLUMNS, columnOrder: COLUMN_ORDER });

// ─── Debounce hook ───────────────────────────────────────────────────────────
function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ─── Pure helpers (defined once, outside component) ─────────────────────────
const padTwo = (n) => n.toString().padStart(2, "0");

function buildBoardData(appointments = []) {
  const tasks = {};
  const columns = {
    "UPCOMING": { id: "UPCOMING", title: "UPCOMING", taskIds: [] },
    "IN-PROGRESS": { id: "IN-PROGRESS", title: "IN-PROGRESS", taskIds: [] },
    "FOR-TESTING": { id: "FOR-TESTING", title: "FOR-TESTING", taskIds: [] },
    "COMPLETED": { id: "COMPLETED", title: "COMPLETED", taskIds: [] },
  };

  for (const appt of appointments) {
    const id = appt._id;
    const date = new Date(appt.dateOfAppointment);
    const h = date.getHours();
    tasks[id] = {
      id,
      content: appt.patientName,
      tag: appt.tags[0],
      time: `${h}:${padTwo(date.getMinutes())} ${h >= 12 ? "PM" : "AM"}`,
      patientId: appt.patientId,
      status: appt.status,
      medicalExpenditureId: appt.medicalExpenditureId,
      invoiceStatus: appt.invoiceStatus ?? null,
    };
    const col = appt.status || "UPCOMING";
    if (columns[col]) columns[col].taskIds.push(id);
  }

  return { tasks, columns, columnOrder: COLUMN_ORDER };
}

function stringToColor(string) {
  if (!string?.length) return null;
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    color += `00${((hash >> (i * 8)) & 0xff).toString(16)}`.slice(-2);
  }
  return color;
}

function stringAvatar(name) {
  if (!name) return { sx: { bgcolor: "#ccc" }, children: "?" };
  return {
    sx: { bgcolor: stringToColor(name) },
    children: name.split(" ")[0]?.[0] ?? "",
  };
}

// ─── Provider ────────────────────────────────────────────────────────────────
export const AppProvider = ({ children }) => {
  const { user, clinicId } = useAuth();

  const today = useRef(new Date().toISOString().slice(0, 10)).current;

  const [boardDate, setBoardDate] = useState(today);
  const [boardData, setBoardData] = useState(EMPTY_BOARD);
  const [organizedAppointments, setOrganizedAppointments] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPatientObj, setSelectedPatientObj] = useState(null);
  const [patientsMHR, setPatientsMHR] = useState([]);
  const [patientsMedicine, setPatientsMedicine] = useState([]);
  const [pastSidebarToggle, setPastSidebarToggle] = useState(false);
  const [cardDropDownCondition, setCardDropDownCondition] = useState("");
  const [medicationGroup, setMedicationGroup] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [doctor, setDoctor] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchQueryLab, setSearchQueryLab] = useState("");
  const [searchMedicineData, setSearchMedicineData] = useState([]);
  const [searchTestLab, setSearchTestLab] = useState([]);
  const [mediTableContent, setMediTableContent] = useState([]);
  const [mediTableContentLabTest, setMediTableContentLabTest] = useState([]);
  const [doctorAdvice, setDoctorAdvice] = useState([]);
  const [diagnosisCards, setDiagnosisCards] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isModalOpenSearchAppointment, setIsModalOpenSearchAppointment] = useState(false);
  const [prescriptionOptions, setPrescriptionOptions] = useState(null);

  // Debounced search values — prevents API call on every keystroke
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const debouncedSearchQueryLab = useDebounce(searchQueryLab, 300);

  // ── Data fetchers ────────────────────────────────────────────────────────
  const fetchPatientObj = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/patients/${id}`);
      setSelectedPatientObj(data?.[0] ?? null);
    } catch (err) {
      logger.error("fetchPatientObj:", err.message);
      setSelectedPatientObj(null);
    }
  }, []);

  const fetchRecordData = useCallback(async (ids) => {
    if (!ids) { setPatientsMHR([]); return; }
    try {
      const { data } = await api.get(`/mhr/${ids}`);
      setPatientsMHR(data);
    } catch (err) {
      logger.error("fetchRecordData:", err.message);
      setPatientsMHR([]);
    }
  }, []);

  const fetchMedicineData = useCallback(async (ids) => {
    if (!ids) { setPatientsMedicine([]); return; }
    try {
      const { data } = await api.get(`/medications/${ids}`);
      setPatientsMedicine(data);
    } catch (err) {
      logger.error("fetchMedicineData:", err.message);
      setPatientsMedicine([]);
    }
  }, []);

  const fetchMedicationGroupData = useCallback(async (ids) => {
    if (!ids) { setMedicationGroup([]); return; }
    try {
      const { data } = await api.get(`/medicationGroups/${ids}`);
      setMedicationGroup(data);
    } catch (err) {
      logger.error("fetchMedicationGroupData:", err.message);
      setMedicationGroup([]);
    }
  }, []);

  const fetchClinicData = useCallback(async (ids) => {
    if (!ids) return;
    try {
      const { data } = await api.get(`/clinics/${ids}`);
      setClinics(data);
      const savedId = clinicId ?? localStorage.getItem("selectedClinic");
      setSelectedClinic(data.find((c) => c._id.toString() === savedId) ?? data[0] ?? null);
    } catch (err) {
      logger.error("fetchClinicData:", err.message);
    }
  }, [clinicId]);

  // Unified axios instance — removed the native fetch inconsistency
  const fetchDoctorData = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/doctors/${id}`);
      setDoctor(data);
    } catch (err) {
      logger.error("fetchDoctorData:", err.message);
    }
    // accessToken no longer needed here; axiosInstance handles auth headers
  }, []);

  const fetchSearchMedicineAPI = useCallback(async (query) => {
    if (!query) { setSearchMedicineData([]); return; }
    try {
      const { data } = await api.get("/pharmaSKU/search/sku", { params: { searchQuery: query } });
      setSearchMedicineData(data);
    } catch (err) {
      logger.error("fetchSearchMedicineAPI:", err.message);
      setSearchMedicineData([]);
    }
  }, []);

  const fetchSearchTestLabAPI = useCallback(async (query) => {
    if (!query) { setSearchTestLab([]); return; }
    try {
      const { data } = await api.get("/testpanel/search", { params: { searchQuery: query } });
      setSearchTestLab(data);
    } catch (err) {
      logger.error("fetchSearchTestLabAPI:", err.message);
      setSearchTestLab([]);
    }
  }, []);

  const fetchPrescriptionOptions = useCallback(async () => {
    try {
      const { data } = await api.get('/mhr/prescriptionRecords/options');
      setPrescriptionOptions(data);
    } catch (err) {
      logger.error('fetchPrescriptionOptions:', err.message);
    }
  }, []);

  const getAppointments = useCallback(async () => {
    if (!selectedClinic?.appointmentUsage || !user) { setSelectedPatient(null); return; }

    const usage = selectedClinic.appointmentUsage.find(
      (u) => u.doctorId.toString() === user._id.toString()
    );
    if (!usage) { setSelectedPatient(null); return; }

    const ids = usage.appointments.map((a) => a.appointmentId.toString()).join(",");
    if (!ids) return;

    try {
      const { data } = await api.get(`/appointments/${ids}`, { params: { appointmentStatus: "ACCEPTED,COMPLETED" }, });

      // Batch-fetch invoice statuses for all appointments that have a medicalExpenditureId
      const expIds = [...new Set(data.map((a) => a.medicalExpenditureId).filter(Boolean))].join(",");
      let invoiceStatusMap = {};
      // if (expIds) {
      //   try {
      //     const { data: exps } = await api.get(`/medicalExpenditures/${expIds}`);
      //     const expArray = Array.isArray(exps) ? exps : [exps];
      //     for (const exp of expArray) {
      //       invoiceStatusMap[exp._id.toString()] = exp.status;
      //     }
      //   } catch (e) {
      //     console.error("Failed to fetch invoice statuses", e);
      //   }
      // }

      const enrichedData = data.map((a) => ({
        ...a,
        invoiceStatus: a.medicalExpenditureId
          ? (invoiceStatusMap[a.medicalExpenditureId.toString()] ?? null)
          : null,
      }));

      setAppointments(enrichedData);

      // Merge into organizedAppointments, updating existing entries with fresh status
      setOrganizedAppointments((prev) => {
        const next = { ...prev };
        for (const appt of enrichedData) {
          const key = appt.dateOfAppointment.split("T")[0];
          const bucket = next[key] ?? [];
          const existingIdx = bucket.findIndex((a) => a._id === appt._id);
          if (existingIdx === -1) {
            next[key] = [...bucket, appt];
          } else {
            const updated = [...bucket];
            updated[existingIdx] = appt;
            next[key] = updated;
          }
        }
        return next;
      });
    } catch (err) {
      logger.error("getAppointments:", err.response?.status ?? err.message);
    }
  }, [selectedClinic, user]);

  // ── Real-time Injection Helper ──────────────────────────────────────────
  const injectNewAppointment = useCallback((newAppt) => {
    const dateKey = newAppt.dateOfAppointment.split("T")[0];

    // 1. Update the flat list (for Calendar dots)
    setAppointments(prev => {
      if (prev.some(a => a._id === newAppt._id)) return prev;
      return [...prev, newAppt];
    });

    // 2. Update the organized bucket (for Kanban Board)
    setOrganizedAppointments(prev => {
      const currentBuckets = { ...prev }; // Clone the whole state object
      const dayBucket = currentBuckets[dateKey] ? [...currentBuckets[dateKey]] : [];

      // Check if appointment already exists to avoid duplicates
      if (dayBucket.some(a => a._id === newAppt._id)) return prev;

      // Add new appointment and update the specific date key
      currentBuckets[dateKey] = [...dayBucket, newAppt];

      return currentBuckets; // Return the new object reference
    });
  }, []);

  // ── THE CRITICAL BRIDGE ──────────────────────────────────────────────
  // Ensure this is the ONLY effect managing boardData from organizedAppointments
  useEffect(() => {
    const slice = organizedAppointments[boardDate] || [];
    // Use buildBoardData to transform the raw array into the Kanban structure
    setBoardData(buildBoardData(slice));
  }, [organizedAppointments, boardDate]);

  // ── Effects ──────────────────────────────────────────────────────────────

  // Persist selected clinic
  useEffect(() => {
    if (selectedClinic) localStorage.setItem("selectedClinic", selectedClinic._id);
  }, [selectedClinic]);

  // Load medication groups
  useEffect(() => {
    if (user?.medicationGroups?.length) {
      fetchMedicationGroupData(user.medicationGroups.join(","));
    }
  }, [user?.medicationGroups, fetchMedicationGroupData]);

  // Load clinics
  useEffect(() => {
    if (user?.clinics?.length) fetchClinicData(user.clinics.join(","));
  }, [user?.clinics, fetchClinicData]);

  // Load appointments when clinic or date changes
  useEffect(() => {
    if (user && selectedClinic) getAppointments();
  }, [boardDate, selectedClinic, user, getAppointments]);

  // Rebuild board data only for the current date slice
  // useEffect(() => {
  //   const slice = organizedAppointments[boardDate];
  //   setBoardData(slice?.length ? buildBoardData(slice) : EMPTY_BOARD);
  // }, [organizedAppointments, boardDate]);

  // Load patient details — reset all prescription state when patient changes
  useEffect(() => {
    if (selectedPatient) {
      setMediTableContent([]);
      setMediTableContentLabTest([]);
      setDoctorAdvice([]);
      setDiagnosisCards([]);
      setSearchQuery('');
      setSearchQueryLab('');
      setSearchMedicineData([]);
      setSearchTestLab([]);
      fetchPatientObj(selectedPatient);
    }
  }, [selectedPatient, fetchPatientObj]);

  // Load patient records & medicines together
  useEffect(() => {
    if (!selectedPatientObj) return;
    const mhrIds = selectedPatientObj.medicalHealthRecords ?? [];
    const medIds = selectedPatientObj.medications ?? [];
    mhrIds.length ? fetchRecordData(mhrIds.join(",")) : setPatientsMHR([]);
    medIds.length ? fetchMedicineData(medIds.join(",")) : setPatientsMedicine([]);
  }, [selectedPatientObj, fetchRecordData, fetchMedicineData]);

  // Load prescription dropdown options once per login
  useEffect(() => {
    if (user) fetchPrescriptionOptions();
  }, [user, fetchPrescriptionOptions]);

  // Debounced medicine search
  useEffect(() => {
    fetchSearchMedicineAPI(debouncedSearchQuery);
  }, [debouncedSearchQuery, fetchSearchMedicineAPI]);

  // Debounced lab search
  useEffect(() => {
    fetchSearchTestLabAPI(debouncedSearchQueryLab);
  }, [debouncedSearchQueryLab, fetchSearchTestLabAPI]);

  // ── Stable context values (split to minimise re-renders) ─────────────────
  const stateValue = useMemo(() => ({
    boardData, boardDate, organizedAppointments, appointments,
    selectedPatient, selectedPatientObj, patientsMHR, patientsMedicine,
    pastSidebarToggle, cardDropDownCondition,
    medicationGroup, selectedClinic, clinics, doctor,
    searchQuery, searchQueryLab, searchMedicineData, searchTestLab,
    mediTableContent, mediTableContentLabTest,
    doctorAdvice, diagnosisCards, selectedAppointment, isModalOpenSearchAppointment,
    prescriptionOptions,
    // pure helpers — stable references, no state dependency
    stringAvatar, stringToColor,
  }), [
    boardData, boardDate, organizedAppointments, appointments,
    selectedPatient, selectedPatientObj, patientsMHR, patientsMedicine,
    pastSidebarToggle, cardDropDownCondition,
    medicationGroup, selectedClinic, clinics, doctor,
    searchQuery, searchQueryLab, searchMedicineData, searchTestLab,
    mediTableContent, mediTableContentLabTest,
    doctorAdvice, diagnosisCards, selectedAppointment, isModalOpenSearchAppointment,
    prescriptionOptions,
  ]);

  const actionValue = useMemo(() => ({
    setBoardData, setBoardDate, setSelectedPatient, setSelectedPatientObj,
    setPastSidebarToggle, setCardDropDownCondition,
    setMedicationGroup, setSelectedClinic, setClinics, setDoctor,
    setSearchQuery, setSearchQueryLab,
    setSearchMedicineData, setSearchTestLab,
    setMediTableContent, setMediTableContentLabTest,
    setPatientsMHR,
    setDoctorAdvice, setDiagnosisCards, setSelectedAppointment,
    setIsModalOpenSearchAppointment, setAppointments,
    getAppointments, fetchClinicData,
    fetchSearchMedicineAPI, fetchSearchTestLabAPI, injectNewAppointment
  }), [getAppointments, fetchClinicData, fetchSearchMedicineAPI, fetchSearchTestLabAPI, injectNewAppointment]);

  return (
    <AppStateContext.Provider value={stateValue}>
      <AppActionContext.Provider value={actionValue}>
        {children}
      </AppActionContext.Provider>
    </AppStateContext.Provider>
  );
};